import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, Search, Loader2, ArrowLeft, AlertTriangle, CheckCircle, XCircle } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { ROMANIAN_COUNTIES, UNIT_CODES } from "@/lib/romanianCounties";
import { validateCuiCnp } from "@/lib/cuiValidation";
import { validateInvoiceForXml } from "@/lib/generateInvoiceXml";
import { uploadInvoiceFiles } from "@/lib/uploadInvoiceFiles";
import AppHeader from "@/components/AppHeader";
import AppFooter from "@/components/AppFooter";

interface LineItem {
  id: string;
  item_name: string;
  quantity: number;
  unit_price: number;
  vat_rate: number;
  unit_code: string;
}

const EditInvoice = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user, loading: authLoading } = useAuth();
  const [saving, setSaving] = useState(false);
  const [showAnafDialog, setShowAnafDialog] = useState(false);
  const [savedInvoiceId, setSavedInvoiceId] = useState<string | null>(null);
  const [cuiLoading, setCuiLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [defaultVatRate, setDefaultVatRate] = useState(0);
  const [defaultUnitCode, setDefaultUnitCode] = useState("C62");
  const [buyerVatRegistered, setBuyerVatRegistered] = useState<boolean | null>(null);
  const [buyerEFactura, setBuyerEFactura] = useState<boolean | null>(null);

  // Form state
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [issueDate, setIssueDate] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [sellerName, setSellerName] = useState("");
  const [sellerCui, setSellerCui] = useState("");
  const [sellerAddress, setSellerAddress] = useState("");
  const [sellerCity, setSellerCity] = useState("");
  const [sellerCounty, setSellerCounty] = useState("");
  const [buyerName, setBuyerName] = useState("");
  const [buyerCui, setBuyerCui] = useState("");
  const [buyerAddress, setBuyerAddress] = useState("");
  const [buyerCity, setBuyerCity] = useState("");
  const [buyerCounty, setBuyerCounty] = useState("");
  const [items, setItems] = useState<LineItem[]>([]);

  useEffect(() => {
    if (!authLoading && !user) navigate("/auth");
  }, [user, authLoading, navigate]);

  // Load profile default VAT rate
  useEffect(() => {
    if (!user) return;
    supabase
      .from("profiles")
      .select("default_vat_rate, default_unit_code")
      .eq("user_id", user.id)
      .single()
      .then(({ data }) => {
        if (data) {
          setDefaultVatRate(Number(data.default_vat_rate) || 0);
          setDefaultUnitCode(data.default_unit_code || "C62");
        }
      });
  }, [user]);

  const { data: invoice, isLoading } = useQuery({
    queryKey: ["invoice", id],
    enabled: !!user && !!id,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("invoices")
        .select("*")
        .eq("id", id!)
        .single();
      if (error) throw error;
      return data;
    },
  });

  const { data: existingItems, isLoading: itemsLoading } = useQuery({
    queryKey: ["invoice-items", id],
    enabled: !!user && !!id,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("invoice_items")
        .select("*")
        .eq("invoice_id", id!)
        .order("sort_order");
      if (error) throw error;
      return data;
    },
  });

  // Populate form once data loads
  useEffect(() => {
    if (!invoice || !existingItems || loaded) return;
    const inv = invoice;
    setInvoiceNumber(inv.invoice_number || "");
    setIssueDate(inv.issue_date || "");
    setDueDate(inv.due_date || "");
    setSellerName(inv.seller_name || "");
    setSellerCui(inv.seller_cui || "");
    setSellerAddress(inv.seller_address || "");
    setSellerCity(inv.seller_city || "");
    setSellerCounty(inv.seller_county || "");
    setBuyerName(inv.buyer_name || "");
    setBuyerCui(inv.buyer_cui || "");
    setBuyerAddress(inv.buyer_address || "");
    setBuyerCity(inv.buyer_city || "");
    setBuyerCounty(inv.buyer_county || "");
    setItems(
      existingItems.map((i: any) => ({
        id: i.id,
        item_name: i.item_name,
        quantity: Number(i.quantity),
        unit_price: Number(i.unit_price),
        vat_rate: Number(i.vat_rate),
        unit_code: i.unit_code || "C62",
      }))
    );
    setLoaded(true);
  }, [invoice, existingItems, loaded]);

  const lookupCUI = async () => {
    const cui = buyerCui.trim();
    if (!cui || cui.replace(/^RO/i, "").length < 2) return;
    setCuiLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("cui-lookup", { body: { cui } });
      if (error) throw error;
      if (data?.error) {
        toast.error(data.error === "CUI not found" ? "CUI-ul nu a fost găsit" : data.error);
        return;
      }
      setBuyerName(data.name || "");
      setBuyerAddress(data.address || "");
      if (data.city) setBuyerCity(data.city);
      if (data.county) setBuyerCounty(data.county);
      setBuyerVatRegistered(data.vat_registered ?? null);
      setBuyerEFactura(data.e_factura ?? null);
      toast.success("Date completate automat de la ANAF!");
    } catch {
      toast.error("Nu s-au putut prelua datele de la ANAF.");
    } finally {
      setCuiLoading(false);
    }
  };

  const addItem = () => {
    setItems([...items, { id: crypto.randomUUID(), item_name: "", quantity: 1, unit_price: 0, vat_rate: defaultVatRate, unit_code: defaultUnitCode }]);
  };

  const removeItem = (itemId: string) => {
    if (items.length === 1) return;
    setItems(items.filter((i) => i.id !== itemId));
  };

  const updateItem = (itemId: string, field: keyof LineItem, value: string | number) => {
    setItems(items.map((i) => (i.id === itemId ? { ...i, [field]: value } : i)));
  };

  const subtotal = items.reduce((s, i) => s + i.quantity * i.unit_price, 0);
  const vatTotal = items.reduce((s, i) => s + i.quantity * i.unit_price * (i.vat_rate / 100), 0);
  const total = subtotal + vatTotal;

  const handleSave = async () => {
    if (!user || !id) return;
    setSaving(true);
    try {
      // Update invoice
      const { error } = await supabase
        .from("invoices")
        .update({
          invoice_number: invoiceNumber || null,
          seller_name: sellerName || null,
          seller_cui: sellerCui || null,
          seller_address: sellerAddress || null,
          seller_city: sellerCity || null,
          seller_county: sellerCounty || null,
          buyer_name: buyerName || null,
          buyer_cui: buyerCui || null,
          buyer_address: buyerAddress || null,
          buyer_city: buyerCity || null,
          buyer_county: buyerCounty || null,
          subtotal,
          vat_total: vatTotal,
          total,
          issue_date: issueDate,
          due_date: dueDate || null,
        } as any)
        .eq("id", id);

      if (error) throw error;

      // Delete old items and re-insert
      await supabase.from("invoice_items").delete().eq("invoice_id", id);

      const lineItems = items
        .filter((i) => i.item_name.trim())
        .map((i, idx) => ({
          invoice_id: id,
          item_name: i.item_name,
          quantity: i.quantity,
          unit_price: i.unit_price,
          vat_rate: i.vat_rate,
          unit_code: i.unit_code,
          sort_order: idx,
        } as any));

      if (lineItems.length) {
        const { error: itemsError } = await supabase.from("invoice_items").insert(lineItems);
        if (itemsError) throw itemsError;
      }

      // Re-upload PDF + XML to storage so accountant export reflects edits
      uploadInvoiceFiles({
        id,
        user_id: user.id,
        invoice_number: invoiceNumber || null,
        issue_date: issueDate,
        due_date: dueDate || null,
        seller_name: sellerName || null,
        seller_cui: sellerCui || null,
        seller_address: sellerAddress || null,
        seller_city: sellerCity || null,
        seller_county: sellerCounty || null,
        buyer_name: buyerName || null,
        buyer_cui: buyerCui || null,
        buyer_address: buyerAddress || null,
        buyer_city: buyerCity || null,
        buyer_county: buyerCounty || null,
        subtotal,
        vat_total: vatTotal,
        total,
        items: items.filter((i) => i.item_name.trim()).map((i) => ({
          item_name: i.item_name,
          quantity: i.quantity,
          unit_price: i.unit_price,
          vat_rate: i.vat_rate,
          unit_code: i.unit_code,
        })),
      }).catch(() => { /* silent */ });

      // Invalidate queries so detail page refreshes
      queryClient.invalidateQueries({ queryKey: ["invoice", id] });
      queryClient.invalidateQueries({ queryKey: ["invoice-items", id] });

      toast.success("Factura a fost actualizată!");
      
      // Show ANAF dialog if invoice was in draft/error
      const status = invoice.status;
      if (status === "draft" || status === "error") {
        setSavedInvoiceId(id);
        setShowAnafDialog(true);
      } else {
        navigate(`/invoice/${id}`);
      }
    } catch (err: any) {
      toast.error(err.message || "Eroare la salvare");
    } finally {
      setSaving(false);
    }
  };

  const handleSendToAnaf = async () => {
    if (!savedInvoiceId) return;

    // Validate using current form state
    const xmlData = {
      invoice_number: invoiceNumber,
      issue_date: issueDate,
      due_date: dueDate,
      seller_name: sellerName,
      seller_cui: sellerCui,
      seller_address: sellerAddress,
      seller_city: sellerCity || null,
      seller_county: sellerCounty || null,
      buyer_name: buyerName,
      buyer_cui: buyerCui,
      buyer_address: buyerAddress,
      buyer_city: buyerCity || null,
      buyer_county: buyerCounty || null,
      subtotal,
      vat_total: vatTotal,
      total,
      items: items.map((i) => ({
        item_name: i.item_name,
        quantity: i.quantity,
        unit_price: i.unit_price,
        vat_rate: i.vat_rate,
        unit_code: i.unit_code,
      })),
    };

    const errors = validateInvoiceForXml(xmlData);
    if (errors.length > 0) {
      toast.error(
        `Câmpuri lipsă — editează factura mai întâi:\n• ${errors.join("\n• ")}`,
        { duration: 8000 }
      );
      setShowAnafDialog(false);
      return;
    }

    try {
      const { error } = await supabase
        .from("invoices")
        .update({ status: "pending" } as any)
        .eq("id", savedInvoiceId);
      if (error) throw error;
      queryClient.invalidateQueries({ queryKey: ["invoice", savedInvoiceId] });
      toast.success("Factura a fost trimisă la ANAF (simulare)!");
    } catch {
      toast.error("Eroare la trimiterea către ANAF");
    }
    setShowAnafDialog(false);
    navigate(`/invoice/${savedInvoiceId}`);
  };

  const handleDismissAnaf = () => {
    setShowAnafDialog(false);
    navigate(`/invoice/${savedInvoiceId || id}`);
  };

  if (authLoading || isLoading || itemsLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="h-8 w-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center gap-4">
        <p className="text-muted-foreground">Factura nu a fost găsită</p>
        <Button variant="outline" onClick={() => navigate("/dashboard")}>Înapoi</Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <AppHeader />

      <main className="container py-6 max-w-xl space-y-8">
          {/* Back + Title */}
          <div className="flex items-center gap-3">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="outline" size="icon" onClick={() => navigate(`/invoice/${id}`)}>
                  <ArrowLeft className="h-5 w-5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Înapoi la factură</TooltipContent>
            </Tooltip>
            <h2 className="font-heading text-xl font-bold">Editează Factura</h2>
          </div>

          {/* Invoice Number & Dates */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Nr. Factură</Label>
              <Input placeholder="EZF-001" value={invoiceNumber} onChange={(e) => setInvoiceNumber(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Data Emiterii</Label>
              <Input type="date" value={issueDate} onChange={(e) => setIssueDate(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Scadență</Label>
              <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
            </div>
          </div>

          {/* Seller Info */}
          <div className="rounded-xl border border-border bg-card p-5 space-y-4">
            <h3 className="font-heading font-semibold">Furnizor</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Denumire</Label>
                <Input value={sellerName} onChange={(e) => setSellerName(e.target.value)} placeholder="Numele firmei" />
              </div>
              <div className="space-y-2">
                <Label>CUI / CNP</Label>
                <Input value={sellerCui} onChange={(e) => setSellerCui(e.target.value)} placeholder="ex: RO12345678 sau CNP"
                  className={(() => { const v = validateCuiCnp(sellerCui); return !v.valid ? "border-destructive focus-visible:ring-destructive" : ""; })()}
                />
                {(() => {
                  const v = validateCuiCnp(sellerCui);
                  if (!v.valid && v.error) return <p className="text-xs text-destructive">{v.error}</p>;
                  if (v.valid && v.type === "cnp" && sellerCui.trim()) return <p className="text-xs text-muted-foreground">Detectat: CNP (persoană fizică)</p>;
                  if (v.valid && v.type === "cui" && sellerCui.trim()) return <p className="text-xs text-muted-foreground">Detectat: CUI (firmă)</p>;
                  return null;
                })()}
              </div>
            </div>
            <div className="space-y-2">
              <Label>Adresă (stradă)</Label>
              <Input value={sellerAddress} onChange={(e) => setSellerAddress(e.target.value)} placeholder="Str. Exemplu nr. 1" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Oraș</Label>
                <Input value={sellerCity} onChange={(e) => setSellerCity(e.target.value)} placeholder="ex: București" />
              </div>
              <div className="space-y-2">
                <Label>Județ</Label>
                <Select value={sellerCounty} onValueChange={setSellerCounty}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selectează județul" />
                  </SelectTrigger>
                  <SelectContent>
                    {ROMANIAN_COUNTIES.map((c) => (
                      <SelectItem key={c.code} value={c.code}>
                        {c.name} ({c.code})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Buyer */}
          <div className="rounded-xl border border-border bg-card p-5 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-heading font-semibold">Client</h3>
              {buyerVatRegistered !== null && (
                <div className="flex gap-2">
                  <Badge variant={buyerVatRegistered ? "default" : "secondary"} className="gap-1 text-xs">
                    {buyerVatRegistered ? <CheckCircle className="h-3 w-3" /> : <XCircle className="h-3 w-3" />}
                    {buyerVatRegistered ? "Plătitor TVA" : "Neplătitor TVA"}
                  </Badge>
                  {buyerEFactura !== null && (
                    <Badge variant={buyerEFactura ? "default" : "outline"} className="gap-1 text-xs">
                      {buyerEFactura ? <CheckCircle className="h-3 w-3" /> : <XCircle className="h-3 w-3" />}
                      {buyerEFactura ? "e-Factura activ" : "e-Factura inactiv"}
                    </Badge>
                  )}
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Label>CUI / CNP Client</Label>
              <div className="flex gap-2">
                <Input
                  placeholder="ex: 12345678, RO12345678, sau CNP"
                  value={buyerCui}
                  onChange={(e) => setBuyerCui(e.target.value)}
                  onBlur={lookupCUI}
                  className={(() => {
                    const v = validateCuiCnp(buyerCui);
                    return !v.valid ? "border-destructive focus-visible:ring-destructive" : "";
                  })()}
                />
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button variant="outline" size="icon" onClick={lookupCUI} disabled={cuiLoading}>
                      {cuiLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Caută datele clientului după CUI</TooltipContent>
                </Tooltip>
              </div>
              {(() => {
                const v = validateCuiCnp(buyerCui);
                if (!v.valid && v.error) return <p className="text-xs text-destructive">{v.error}</p>;
                if (v.valid && v.type === "cnp" && buyerCui.trim()) return <p className="text-xs text-muted-foreground">Detectat: CNP (persoană fizică)</p>;
                if (v.valid && v.type === "cui" && buyerCui.trim()) return <p className="text-xs text-muted-foreground">Detectat: CUI (firmă)</p>;
                return null;
              })()}
            </div>
            <div className="space-y-2">
              <Label>Denumire</Label>
              <Input value={buyerName} onChange={(e) => setBuyerName(e.target.value)} placeholder="Se completează automat" />
            </div>
            <div className="space-y-2">
              <Label>Adresă (stradă)</Label>
              <Input value={buyerAddress} onChange={(e) => setBuyerAddress(e.target.value)} placeholder="Se completează automat" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Oraș</Label>
                <Input value={buyerCity} onChange={(e) => setBuyerCity(e.target.value)} placeholder="ex: Cluj-Napoca" />
              </div>
              <div className="space-y-2">
                <Label>Județ</Label>
                <Select value={buyerCounty} onValueChange={setBuyerCounty}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selectează județul" />
                  </SelectTrigger>
                  <SelectContent>
                    {ROMANIAN_COUNTIES.map((c) => (
                      <SelectItem key={c.code} value={c.code}>
                        {c.name} ({c.code})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Line Items */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-heading font-semibold">Produse / Servicii</h3>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="outline" size="sm" onClick={addItem}>
                    <Plus className="h-4 w-4 mr-1" /> Adaugă
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Adaugă un produs sau serviciu nou</TooltipContent>
              </Tooltip>
            </div>
            {items.map((item, idx) => (
              <div key={item.id} className="rounded-xl border border-border bg-card p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">#{idx + 1}</span>
                  {items.length > 1 && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => removeItem(item.id)}>
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Șterge acest produs</TooltipContent>
                    </Tooltip>
                  )}
                </div>
                <div className="space-y-2">
                  <Label>Denumire</Label>
                  <Input
                    placeholder="ex: Servicii consultanță"
                    value={item.item_name}
                    onChange={(e) => updateItem(item.id, "item_name", e.target.value)}
                  />
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div className="space-y-2">
                    <Label>Cantitate</Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={item.quantity}
                      onChange={(e) => updateItem(item.id, "quantity", parseFloat(e.target.value) || 0)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>U.M.</Label>
                    <Select value={item.unit_code} onValueChange={(v) => updateItem(item.id, "unit_code", v)}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {UNIT_CODES.map((u) => (
                          <SelectItem key={u.code} value={u.code}>{u.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Preț Unitar</Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={item.unit_price}
                      onChange={(e) => updateItem(item.id, "unit_price", parseFloat(e.target.value) || 0)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>TVA %</Label>
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      value={item.vat_rate}
                      onChange={(e) => updateItem(item.id, "vat_rate", parseFloat(e.target.value) || 0)}
                    />
                  </div>
                </div>
                <div className="text-right text-sm text-muted-foreground">
                  Subtotal: {(item.quantity * item.unit_price).toLocaleString("ro-RO", { style: "currency", currency: "RON" })}
                </div>
              </div>
            ))}
          </div>

          {/* Totals */}
          <div className="rounded-xl border border-primary/30 bg-primary/5 p-5 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Subtotal</span>
              <span>{subtotal.toLocaleString("ro-RO", { style: "currency", currency: "RON" })}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">TVA</span>
              <span>{vatTotal.toLocaleString("ro-RO", { style: "currency", currency: "RON" })}</span>
            </div>
            <div className="border-t border-border pt-2 flex justify-between font-heading font-bold text-lg">
              <span>Total</span>
              <span className="text-primary">{total.toLocaleString("ro-RO", { style: "currency", currency: "RON" })}</span>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pb-8">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="outline" className="flex-1 h-12" onClick={() => navigate(`/invoice/${id}`)}>
                  Anulează
                </Button>
              </TooltipTrigger>
              <TooltipContent>Renunță la modificări și revino la factură</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button className="flex-1 h-12 font-semibold" onClick={handleSave} disabled={saving}>
                  {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                  Salvează Modificările
                </Button>
              </TooltipTrigger>
              <TooltipContent>Salvează toate modificările facturii</TooltipContent>
            </Tooltip>
          </div>

          {/* ANAF Send Dialog */}
          <Dialog open={showAnafDialog} onOpenChange={(open) => { if (!open) handleDismissAnaf(); }}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-primary" />
                  Trimite la ANAF?
                </DialogTitle>
                <DialogDescription className="text-left space-y-2 pt-2">
                  <p>Factura a fost salvată cu succes. Vrei să o trimiți acum la ANAF?</p>
                  <p className="text-xs text-muted-foreground">Integrarea ANAF SPV nu este încă activă — factura va fi marcată ca „În așteptare".</p>
                </DialogDescription>
              </DialogHeader>
              <DialogFooter className="gap-2 sm:gap-0">
                <Button variant="outline" onClick={handleDismissAnaf}>Nu, mai târziu</Button>
                <Button onClick={handleSendToAnaf}>Da, trimite</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
      </main>
      <AppFooter />
    </div>
  );
};

export default EditInvoice;
